/**
 * Link-level UI helpers — thin display layer over the `connection-stats` business core.
 *
 * The 5-level scale mapping itself (normalizeLevel, worseLevel, rttToLevel, lossToLevel,
 * transportToLevel, scoreLinkLevel, HISTORY_LEN) lives one level up, in `..`
 * (`level-scale.ts`); this file re-exports it and adds the display-only helpers
 * (labels, CSS chip class, sparkline metrics) used by the UI layer.
 */

import {
  HISTORY_LEN,
  normalizeLevel,
  worseLevel,
  rttToLevel,
  lossToLevel,
  transportToLevel,
  scoreLinkLevel,
} from "..";
import type { LinkLevel, LinkQualityScoreInput } from "..";

export {
  HISTORY_LEN,
  normalizeLevel,
  worseLevel,
  rttToLevel,
  lossToLevel,
  transportToLevel,
  scoreLinkLevel,
};
export type { LinkLevel, LinkQualityScoreInput };

const LEVEL_LABEL_RU: Record<string, string> = {
  good: "хорошо",
  normal: "норм",
  degraded: "деград",
  bad: "плохо",
  critical: "критично",
  warn: "норм",
  unknown: "—",
  muted: "—",
};

export function levelLabelRu(level: string | null | undefined): string {
  const n = normalizeLevel(level);
  return LEVEL_LABEL_RU[n] || "—";
}

/** CSS modifier for StatusChip (status-chip--*).
 * Returns "" for null/undefined — no modifier class, value gets default text color. */
export function chipLevelClass(level: string | null | undefined): string {
  if (level == null) return "";
  return normalizeLevel(level);
}

/** Scale helpers for dual spark (height %). */
export const SPARK_LOSS_MAX_PCT = 20;
export const SPARK_RTT_MAX_MS = 800;

/**
 * Health bar height for sparklines: taller = healthier (lower RTT / loss).
 * Color still comes from thresholds; height is inverted magnitude.
 *
 * @returns height 28..100 or null if missing
 */
export function sparkHeightPct(value: number | null | undefined, max: number): number | null {
  if (value == null || !Number.isFinite(Number(value)) || max <= 0) return null;
  const badness = Math.max(0, Math.min(1, Number(value) / max));
  const health = 1 - badness;
  return Math.round(28 + health * 72);
}
